import React, { useEffect, useState } from 'react';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import Badge from '@cloudscape-design/components/badge';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Modal from '@cloudscape-design/components/modal';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Alert from '@cloudscape-design/components/alert';
import Checkbox from '@cloudscape-design/components/checkbox';
import Spinner from '@cloudscape-design/components/spinner';
import TextFilter from '@cloudscape-design/components/text-filter';
import { getMasterService } from '../services';
import type { BrandMember, BrandEntity } from '../services/types';

// ── Entity-grant editor (per member) ──────────────────────────────────────────
function GrantsModal({
    member,
    onClose,
}: {
    member: BrandMember;
    onClose: () => void;
}) {
    const [entities, setEntities] = useState<BrandEntity[] | null>(null);
    const [granted, setGranted] = useState<Record<string, boolean>>({});
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [q, setQ] = useState('');

    useEffect(() => {
        let active = true;
        Promise.all([
            getMasterService().listEntities(),
            getMasterService().getMemberGrants(member.username),
        ])
            .then(([ents, grantedNos]) => {
                if (!active) return;
                setEntities(ents);
                setGranted(Object.fromEntries(grantedNos.map((n) => [n, true])));
            })
            .catch((e) => active && setError(e instanceof Error ? e.message : 'Load failed.'));
        return () => {
            active = false;
        };
    }, [member.username]);

    const save = () => {
        if (!entities) return;
        setSaving(true);
        setError(null);
        // Send a decision for every brand entity (explicit Y/N), so clearing a
        // box revokes rather than leaving a stale grant.
        const decisions: Record<string, boolean> = Object.fromEntries(
            entities.map((e) => [e.supplierNo, !!granted[e.supplierNo]]),
        );
        getMasterService()
            .setMemberGrants(member.username, decisions)
            .then(() => onClose())
            .catch((e) => setError(e instanceof Error ? e.message : 'Save failed.'))
            .finally(() => setSaving(false));
    };

    const query = q.trim().toLowerCase();
    const shown = (entities ?? []).filter(
        (e) =>
            query === '' ||
            e.displayLabel.toLowerCase().includes(query) ||
            e.supplierNo.includes(query),
    );
    const grantedCount = Object.values(granted).filter(Boolean).length;

    return (
        <Modal
            visible
            onDismiss={onClose}
            header={`Entity access · ${member.email}`}
            footer={
                <Box float="right">
                    <SpaceBetween size="xs" direction="horizontal">
                        <Button variant="link" onClick={onClose} disabled={saving}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={save}
                            loading={saving}
                            disabled={entities === null}
                        >
                            Save access
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween size="m">
                <Box color="text-body-secondary">
                    Choose which legal entities this member can submit ASNs for.{' '}
                    {entities ? `${grantedCount} of ${entities.length} granted.` : null}
                </Box>
                {error ? <Alert type="error">{error}</Alert> : null}
                {entities === null ? <Spinner /> : null}
                {entities !== null && entities.length === 0 ? (
                    <Box color="text-body-secondary">
                        No legal entities are loaded for your brand yet.
                    </Box>
                ) : null}
                {entities !== null && entities.length > 0 ? (
                    <SpaceBetween size="s">
                        <SpaceBetween size="xs" direction="horizontal">
                            <Button
                                onClick={() =>
                                    setGranted(
                                        Object.fromEntries(entities.map((e) => [e.supplierNo, true])),
                                    )
                                }
                            >
                                Grant all
                            </Button>
                            <Button onClick={() => setGranted({})}>Clear all</Button>
                        </SpaceBetween>
                        <TextFilter
                            filteringText={q}
                            filteringPlaceholder="Filter entities…"
                            onChange={({ detail }) => setQ(detail.filteringText)}
                        />
                        {shown.map((e) => (
                            <Checkbox
                                key={e.supplierNo}
                                checked={!!granted[e.supplierNo]}
                                onChange={({ detail }) =>
                                    setGranted((prev) => ({
                                        ...prev,
                                        [e.supplierNo]: detail.checked,
                                    }))
                                }
                            >
                                <Box>
                                    <Box fontWeight="bold" display="inline">
                                        {e.supplierNo}
                                    </Box>{' '}
                                    <Box color="text-body-secondary" fontSize="body-s" display="inline">
                                        {e.displayLabel}
                                    </Box>
                                </Box>
                            </Checkbox>
                        ))}
                    </SpaceBetween>
                ) : null}
            </SpaceBetween>
        </Modal>
    );
}

/**
 * Team Members — a brand MASTER adds and manages teammates (MEMBERs) without an
 * admin round-trip. Members share the brand's one API credential; inviting one
 * sends a Cognito invitation email with a temporary password. The brand, role,
 * and domain are resolved server-side from the token.
 */
export default function TeamMembersScreen() {
    const [members, setMembers] = useState<BrandMember[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [inviteOpen, setInviteOpen] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviting, setInviting] = useState(false);
    const [inviteError, setInviteError] = useState<string | null>(null);

    const [busyUser, setBusyUser] = useState<string | null>(null);
    const [grantsFor, setGrantsFor] = useState<BrandMember | null>(null);

    const refresh = () => {
        setLoading(true);
        setLoadError(null);
        getMasterService()
            .listMembers()
            .then(setMembers)
            .catch((e) => setLoadError(e instanceof Error ? e.message : 'Failed to load members.'))
            .finally(() => setLoading(false));
    };

    useEffect(refresh, []);

    const submitInvite = () => {
        const email = inviteEmail.trim();
        if (!email) return;
        setInviting(true);
        setInviteError(null);
        getMasterService()
            .inviteMember(email)
            .then(() => {
                setInviteOpen(false);
                setInviteEmail('');
                refresh();
            })
            .catch((e) =>
                setInviteError(e instanceof Error ? e.message : 'Failed to invite member.'),
            )
            .finally(() => setInviting(false));
    };

    const toggle = (m: BrandMember) => {
        setBusyUser(m.username);
        const svc = getMasterService();
        const action = m.enabled ? svc.disableMember(m.username) : svc.enableMember(m.username);
        action.then(refresh).finally(() => setBusyUser(null));
    };

    return (
        <SpaceBetween size="l">
            {loadError ? <Alert type="error">{loadError}</Alert> : null}
            <Table
                variant="container"
                loading={loading}
                items={members}
                header={
                    <Header
                        counter={`(${members.length})`}
                        actions={
                            <Button variant="primary" onClick={() => setInviteOpen(true)}>
                                Invite member
                            </Button>
                        }
                    >
                        Team members
                    </Header>
                }
                empty={
                    <Box textAlign="center" color="text-body-secondary" padding="l">
                        No teammates yet. Invite your first member.
                    </Box>
                }
                columnDefinitions={[
                    {
                        id: 'email',
                        header: 'Member',
                        cell: (m) => <Box fontWeight="bold">{m.email || m.username}</Box>,
                    },
                    {
                        id: 'role',
                        header: 'Role',
                        cell: (m) => (
                            <Badge color={m.role === 'MASTER' ? 'green' : 'blue'}>{m.role}</Badge>
                        ),
                    },
                    {
                        id: 'status',
                        header: 'Status',
                        cell: (m) => (
                            <StatusIndicator type={m.enabled ? 'success' : 'stopped'}>
                                {m.enabled ? m.status || 'Active' : 'Disabled'}
                            </StatusIndicator>
                        ),
                    },
                    {
                        id: 'actions',
                        header: '',
                        cell: (m) =>
                            m.role === 'MASTER' ? (
                                <Box color="text-body-secondary">All entities</Box>
                            ) : (
                                <SpaceBetween size="xs" direction="horizontal">
                                    <Button variant="normal" onClick={() => setGrantsFor(m)}>
                                        Manage access
                                    </Button>
                                    <Button
                                        variant="normal"
                                        loading={busyUser === m.username}
                                        disabled={busyUser !== null}
                                        onClick={() => toggle(m)}
                                    >
                                        {m.enabled ? 'Disable' : 'Enable'}
                                    </Button>
                                </SpaceBetween>
                            ),
                    },
                ]}
            />

            {inviteOpen ? (
                <Modal
                    visible
                    onDismiss={() => setInviteOpen(false)}
                    header="Invite a team member"
                    footer={
                        <Box float="right">
                            <SpaceBetween size="xs" direction="horizontal">
                                <Button
                                    variant="link"
                                    onClick={() => setInviteOpen(false)}
                                    disabled={inviting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={submitInvite}
                                    loading={inviting}
                                    disabled={inviteEmail.trim().length === 0}
                                >
                                    Send invite
                                </Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    <SpaceBetween size="m">
                        <Box color="text-body-secondary">
                            The member must use an email on your brand&apos;s domain. They&apos;ll
                            get an email with a temporary password and share your brand&apos;s API
                            credential.
                        </Box>
                        {inviteError ? <Alert type="error">{inviteError}</Alert> : null}
                        <FormField label="Member email">
                            <Input
                                value={inviteEmail}
                                placeholder="teammate@yourbrand.com"
                                type="email"
                                onChange={({ detail }) => setInviteEmail(detail.value)}
                            />
                        </FormField>
                    </SpaceBetween>
                </Modal>
            ) : null}

            {grantsFor ? (
                <GrantsModal member={grantsFor} onClose={() => setGrantsFor(null)} />
            ) : null}
        </SpaceBetween>
    );
}
